import { mdiMagnify } from "@mdi/js";
import Icon from "@mdi/react";
import { clsx } from "clsx";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

import Button from "components/Button";
import Input from "components/Input";
import { RefLoadingSpinner } from "components/LoadingSpinner";
import Select from "components/Select";
import { INVITED_CARRIER_SEARCH_FILTER_LIST } from "constants/haulpay-broker";
import { routes } from "constants/routes";
import { usePullToRefresh } from "context/PullToRefreshContext";
import InvitedCarrierItem from "features/main/HaulPay/Shared/InvitedCarrierItem";
import useDeviceType from "hooks/useDeviceType";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { IInvitedCarrier } from "models/HaulPay";
import { getInvitedCarriersAPI } from "services/api/haulpay";
import { selectUserDetails } from "store/user/selector";
import styles from "./style.module.scss";

interface ISearchForm {
  searchText: string;
  searchType: string;
}

export default function BrokerInvitedCarrier() {
  const navigate = useNavigate();
  const userDetails = useSelector(selectUserDetails);
  const groups = userDetails?.groups ?? [];
  const { isDesktop } = useDeviceType();
  const [filterValue, setFilterValue] = useState(INVITED_CARRIER_SEARCH_FILTER_LIST[0]);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm<ISearchForm>({
    defaultValues: {
      searchText: "",
      searchType: filterValue.value
    }
  });

  const fetchInvitedCarriers = async (params: any) => {
    return getInvitedCarriersAPI(params.group_id, params.limit, params.offset);
  };

  const {
    data: invitedCarriers,
    loading: isLoadingMore,
    observerRef,
    deleteItem,
    resetPagination,
    fetch: fetchInvitedPage
  } = useInfiniteScroll<IInvitedCarrier>(
    fetchInvitedCarriers,
    {
      threshold: 0.9,
      group_id: groups[0]?.id
    },
    [groups[0]?.id]
  );

  usePullToRefresh(() => fetchInvitedPage({ offset: 0, add: false }), {
    enabled: Boolean(groups[0]?.id)
  });

  function goToAddNew() {
    navigate(routes.HAULPAY_BROKER_CARRIERS_NEW);
  }

  const onInvitationDeleted = (id: string) => {
    deleteItem(id);
  };

  const onSearch = async () => {
    // Implement search filtering logic here
    await resetPagination();
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-2 desktop:gap-6 mobile:flex-col-reverse">
        <form className="flex desktop:w-2/3 gap-2 align-center" onSubmit={handleSubmit(onSearch)}>
          <Input
            {...register("searchText")}
            prefixNode={<Icon path={mdiMagnify} className={clsx(styles.searchButton)} />}
            placeholder="Search for #"
            className="w-fit mobile:w-full"
            autoComplete="off"
          />
          <Select
            placeholder="Carrier Name"
            options={INVITED_CARRIER_SEARCH_FILTER_LIST}
            value={filterValue}
            onChange={(option) => {
              setFilterValue(option);
            }}
            btnClass="!!pl-3 desktop:!pr-20 mobile:!pr-10"
            className="w-fit"
            arrowClass="!right-1"
          />
          <Button
            type="submit"
            variant="outline"
            className="!h-12 mobile:text-xs desktop:!px-12 mobile:!px-6 desktop:!rounded-base"
            loading={isSubmitting}>
            Search
          </Button>
        </form>
        <div className="flex desktop:w-1/3 justify-end">
          <Button
            className="!h-10 desktop:!h-12 mobile:text-xs desktop:!px-12 !px-9 desktop:!rounded-base"
            onClick={goToAddNew}>
            {isDesktop ? "Add New" : "ADD NEW"}
          </Button>
        </div>
      </div>

      <div>
        {invitedCarriers.map((item) => (
          <InvitedCarrierItem
            key={item.id}
            data={item}
            onInvitationDeleted={() => onInvitationDeleted(item.id)}
          />
        ))}
        <RefLoadingSpinner observerRef={observerRef} isLoadingMore={isLoadingMore} />
      </div>
    </div>
  );
}
